import { useCallback, useRef } from 'react';
import { aiCandidateAPI } from '../../../lib/aiInterviewApi';
import { pickMime, sleep } from '../media';

// Short non-reversible tag so the interview link token itself never sits in localStorage.
const tokenTag = (t) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < String(t).length; i += 1) h = Math.imul(h ^ String(t).charCodeAt(i), 0x01000193);
  return (h >>> 0).toString(16);
};

// Continuous audio+video recording, uploaded in ordered 10-second chunks with retries.
export function useSessionRecorder(token) {
  const rec = useRef(null);
  const startAt = useRef(0);
  const part = useRef(0);
  const seq = useRef(0);
  const queue = useRef(Promise.resolve());

  const upload = useCallback(async (blob, p, s) => {
    for (let i = 0; i < 4; i += 1) {
      try {
        const fd = new FormData();
        fd.append('chunk', blob, `${s}.webm`);
        fd.append('part', String(p));
        fd.append('seq', String(s));
        await aiCandidateAPI.chunk(token, fd);
        return;
      } catch (e) {
        const st = e.response?.status;
        if (st && st < 500 && st !== 429) return;
        await sleep(1500 * (i + 1));
      }
    }
  }, [token]);

  const start = useCallback((stream, clockMs = 0) => {
    if (rec.current || typeof window.MediaRecorder === 'undefined') return;
    const key = `chire-iv-part-${tokenTag(token)}`;
    const legacy = `chire-iv-part-${token}`;
    const prev = Math.max(Number(localStorage.getItem(key) ?? '-1'), Number(localStorage.getItem(legacy) ?? '-1'));
    localStorage.removeItem(legacy);
    const p = Math.min(50, Math.max(prev + 1, Math.floor(clockMs / 60000)));
    localStorage.setItem(key, String(p));
    part.current = p;
    seq.current = 0;
    const mime = pickMime(['video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']);
    try {
      const r = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: 500000, audioBitsPerSecond: 64000 });
      r.ondataavailable = (e) => {
        if (!e.data || !e.data.size) return;
        const s = seq.current;
        seq.current += 1;
        queue.current = queue.current.then(() => upload(e.data, p, s));
      };
      r.start(10000);
      startAt.current = performance.now();
      rec.current = r;
    } catch (e) {
      rec.current = null;
    }
  }, [token, upload]);

  const offsetMs = useCallback(() => (rec.current ? Math.round(performance.now() - startAt.current) : null), []);

  const stop = useCallback(async () => {
    const r = rec.current;
    if (r && r.state !== 'inactive') {
      await new Promise((res) => { r.addEventListener('stop', res, { once: true }); r.stop(); });
    }
    rec.current = null;
    await Promise.race([queue.current, sleep(15000)]);
  }, []);

  return { start, stop, offsetMs, part };
}
